import { hasAdminProfile } from '../../utils/profileRole';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  GetApp as GetAppIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  SmartToy as SmartToyIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  UploadFile as UploadIcon,
  Visibility as VisibilityIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import { toast } from 'react-toastify';
import * as aiApi from '../../services/aiDocumentsService';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectPermissions } from '../../redux/user/user.actions';
import FeedbackAnalytics from '../../components/FeedbackAnalytics';
import ConsumoDeIA from '../../components/ConsumoDeIA';

function useIsAdmin() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return hasAdminProfile(u) || Boolean(u && u.is_super_admin);
  } catch {
    return false;
  }
}

function formatTags(tags) {
  if (tags == null) return '—';
  if (Array.isArray(tags)) return tags.length ? tags.join(', ') : '—';
  return String(tags);
}

function parseDocTags(raw) {
  if (Array.isArray(raw)) {
    return raw.map((t) => String(t).trim().toLowerCase()).filter(Boolean);
  }
  if (raw == null || raw === '') return [];
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) {
        return p.map((t) => String(t).trim().toLowerCase()).filter(Boolean);
      }
    } catch {
      return raw
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
    }
  }
  return [];
}

function mergePendingTagInput(tagsArr, pendingInput) {
  const base = Array.isArray(tagsArr) ? [...tagsArr] : parseDocTags(tagsArr);
  const p = String(pendingInput || '').trim().toLowerCase();
  if (p && !base.includes(p)) base.push(p);
  return [...new Set(base)];
}

const UPLOAD_ACCEPT = '.pdf,.txt,.doc,.docx';
const UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
const UPLOAD_MIME_TYPES = new Set([
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

function validateUploadFile(file) {
  if (!UPLOAD_MIME_TYPES.has(file.type)) {
    return 'Tipo não suportado. Use PDF, TXT, DOC ou DOCX.';
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    return 'Arquivo muito grande (máx. 10 MB).';
  }
  return null;
}

function formatBytes(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return '—';
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  return `${(n / 1024).toFixed(0)} KB`;
}

function formatDocDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-PT');
  } catch {
    return String(iso);
  }
}

function newUploadEntryId() {
  return `up-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function uploadEntryKey(entry) {
  return entry.id || `${entry.file.name}-${entry.file.size}`;
}

function isUploadReady(entry) {
  return !entry.checking && entry.mode !== 'duplicate';
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_ROWS_PER_PAGE = 25;

const KB_FILTER_FIELD_SX = {
  '& .MuiInputBase-root': {
    height: 40,
    boxSizing: 'border-box',
  },
};

function scoreChipProps(score) {
  const s = Number(score);
  if (s >= 0.75) return { label: s.toFixed(4), color: 'success', variant: 'filled' };
  if (s >= 0.55) return { label: s.toFixed(4), color: 'warning', variant: 'filled' };
  return { label: s.toFixed(4), color: 'default', variant: 'outlined' };
}

export default function KnowledgeBasePage() {
  const { theme } = useContext(ThemeContext);
  const isAdmin = useIsAdmin();
  const [tab, setTab] = useState(0);
  const permissions = useSelector(selectPermissions);
  // Publicar para toda a plataforma e privilegio de super-admin: o
  // documento passa a aparecer nas buscas de TODOS os clientes. A API
  // recusa de qualquer jeito (403); esconder aqui evita oferecer o que
  // nao se pode fazer.
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = !!(currentUser && currentUser.is_super_admin);
  const feedbackUser = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
  const canReviewFeedback = (Array.isArray(permissions) && permissions.includes('#avaliar-feedback-ia'))
    || hasAdminProfile(feedbackUser) || Boolean(feedbackUser?.is_super_admin);

  const [includeInactive, setIncludeInactive] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [indexByDoc, setIndexByDoc] = useState({});
  const [loadingIndexBatch, setLoadingIndexBatch] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailDoc, setDetailDoc] = useState(null);
  const [detailStatus, setDetailStatus] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState('create');
  const [editDocId, setEditDocId] = useState(null);
  const [form, setForm] = useState({
    title: '',
    content: '',
    category: '',
    keywords: '',
    tags: [],
    active: true,
    order: 0,
    // null = deixa a API decidir pela regra de sempre (com empresa, da
    // empresa; sem empresa, do tenant). So vira 'platform' por escolha
    // deliberada de um super-admin.
    scope: null,
  });
  const [saving, setSaving] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadMeta, setUploadMeta] = useState({ category: '', tags: '', active: true });
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState(null);
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);
  // Painel para gerenciar todos os duplicados num só lugar (bulk ou 1 a 1).
  const [manageDuplicatesOpen, setManageDuplicatesOpen] = useState(false);

  const [reprocessId, setReprocessId] = useState(null);
  const [confirmReprocessAll, setConfirmReprocessAll] = useState(false);
  const [reprocessAllLoading, setReprocessAllLoading] = useState(false);
  const [confirmReprocessPending, setConfirmReprocessPending] = useState(false);
  const [reprocessPendingLoading, setReprocessPendingLoading] = useState(false);
  const [reprocessPendingProgress, setReprocessPendingProgress] = useState(null); // { done, total, failed }

  const [deleteTarget, setDeleteTarget] = useState(null);

  const [tagCatalog, setTagCatalog] = useState([]);
  const [categoryCatalog, setCategoryCatalog] = useState([]);
  const [selectedFilterTags, setSelectedFilterTags] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [docTagInput, setDocTagInput] = useState('');

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    changeCategory: false,
    category: '',
    changeTags: false,
    tags: [],
    tagMode: 'replace',
  });
  const [bulkEditTagInput, setBulkEditTagInput] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkReprocessOpen, setBulkReprocessOpen] = useState(false);
  const [bulkReprocessLoading, setBulkReprocessLoading] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkActionsAnchor, setBulkActionsAnchor] = useState(null);
  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuDoc, setRowMenuDoc] = useState(null);

  const openRowMenu = (event, doc) => {
    setRowMenuAnchor(event.currentTarget);
    setRowMenuDoc(doc);
  };

  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuDoc(null);
  };

  const loadCatalogs = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([aiApi.listTags(), aiApi.listCategories()]);
      if (t?.success) setTagCatalog(t.tags || []);
      if (c?.success) setCategoryCatalog(Array.isArray(c.categories) ? c.categories : []);
    } catch {}
  }, []);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  const loadDocuments = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const data = await aiApi.listDocuments({ includeInactive });
      if (data.success) setDocuments(data.documents || []);
      else toast.error(data.error || 'Erro ao listar documentos');
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.errorMessage || 'Erro ao listar documentos');
    } finally {
      setLoadingDocs(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const loadIndexBatch = useCallback(async (docs) => {
    if (!docs?.length) {
      setIndexByDoc({});
      return;
    }
    setLoadingIndexBatch(true);
    try {
      const chunk = 100;
      for (let i = 0; i < docs.length; i += chunk) {
        const slice = docs.slice(i, i + chunk);
        try {
          const resp = await aiApi.getIndexStatusBatch(slice.map((d) => d.id));
          const batch = {};
          (resp?.statuses || []).forEach((r) => {
            if (r?.success) batch[r.document_id] = r;
          });
          setIndexByDoc((prev) => ({ ...prev, ...batch }));
        } catch {
          /* ignora o chunk com erro; os demais seguem */
        }
      }
    } finally {
      setLoadingIndexBatch(false);
    }
  }, []);

  useEffect(() => {
    if (documents.length) loadIndexBatch(documents);
  }, [documents, loadIndexBatch]);

  const mergedTagFilterOptions = useMemo(() => {
    const set = new Set((tagCatalog || []).map((t) => String(t).toLowerCase()));
    documents.forEach((d) => parseDocTags(d.tags).forEach((t) => set.add(t)));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [tagCatalog, documents]);

  const mergedCategoryFilterOptions = useMemo(() => {
    const set = new Set((categoryCatalog || []).map((c) => String(c)));
    documents.forEach((d) => {
      if (d.category) set.add(String(d.category));
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [categoryCatalog, documents]);

  const tagOptionsForForm = useMemo(() => {
    const set = new Set(mergedTagFilterOptions);
    (form.tags || []).forEach((t) => set.add(String(t).toLowerCase()));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [mergedTagFilterOptions, form.tags]);

  const categoryOptionsForForm = useMemo(() => {
    const set = new Set(mergedCategoryFilterOptions);
    if (form.category) set.add(String(form.category));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [mergedCategoryFilterOptions, form.category]);

  const tagOptionsForBulk = useMemo(() => {
    const set = new Set(mergedTagFilterOptions);
    (bulkEditForm.tags || []).forEach((t) => set.add(String(t).toLowerCase()));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [mergedTagFilterOptions, bulkEditForm.tags]);

  const filteredDocuments = useMemo(() => {
    let list = documents;
    const qRaw = nameFilter.trim();
    const q = qRaw.toLowerCase();
    if (q) {
      const qDigits = /^\d+$/.test(qRaw);
      list = list.filter((d) => {
        const title = String(d.title || '').toLowerCase();
        const fileName = String(d.original_filename || '').toLowerCase();
        const idStr = String(d.id ?? '');
        const idMatch =
          qDigits &&
          (idStr === qRaw || idStr.includes(qRaw) || Number(d.id) === Number(qRaw));
        return title.includes(q) || fileName.includes(q) || idMatch;
      });
    }
    if (selectedFilterTags.length) {
      const wanted = new Set(selectedFilterTags.map((t) => String(t).toLowerCase()));
      list = list.filter((d) => {
        const docTags = parseDocTags(d.tags);
        return docTags.some((t) => wanted.has(t));
      });
    }
    if (filterCategory) {
      list = list.filter((d) => String(d.category || '') === filterCategory);
    }
    return list;
  }, [documents, nameFilter, selectedFilterTags, filterCategory]);

  const documentSortTimestamp = (doc) => {
    const created = doc.created_at ? new Date(doc.created_at).getTime() : NaN;
    const updated = doc.updated_at ? new Date(doc.updated_at).getTime() : NaN;
    if (!Number.isNaN(created)) return created;
    if (!Number.isNaN(updated)) return updated;
    return doc.id ?? 0;
  };

  const sortedDocuments = useMemo(() => {
    const list = [...filteredDocuments];
    switch (sortBy) {
      case 'oldest':
        return list.sort((a, b) => documentSortTimestamp(a) - documentSortTimestamp(b));
      case 'id_asc':
        return list.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      case 'id_desc':
        return list.sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
      case 'newest':
      default:
        return list.sort((a, b) => documentSortTimestamp(b) - documentSortTimestamp(a));
    }
  }, [filteredDocuments, sortBy]);

  const paginatedDocuments = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedDocuments.slice(start, start + rowsPerPage);
  }, [sortedDocuments, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [nameFilter, selectedFilterTags, filterCategory, sortBy, includeInactive]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sortedDocuments.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [sortedDocuments.length, rowsPerPage, page]);

  const hasListFilters = !!(nameFilter.trim() || selectedFilterTags.length || filterCategory);

  const clearListFilters = () => {
    setNameFilter('');
    setSelectedFilterTags([]);
    setFilterCategory('');
  };

  const selectedDocs = useMemo(
    () => documents.filter((d) => selectedIds.includes(d.id)),
    [documents, selectedIds]
  );

  // Documentos com conteúdo mas ainda NÃO totalmente indexados (Pendente/Parcial).
  // "Sem conteúdo" e "Indexado" ficam de fora — não há o que (re)processar.
  const pendingDocs = useMemo(
    () =>
      documents.filter((d) => {
        const s = indexByDoc[d.id];
        if (!s?.success || !s.has_content) return false;
        return !(s.chunks_count > 0 && s.has_qdrant_points);
      }),
    [documents, indexByDoc]
  );

  const visibleAllSelected = useMemo(
    () =>
      paginatedDocuments.length > 0 && paginatedDocuments.every((d) => selectedIds.includes(d.id)),
    [paginatedDocuments, selectedIds]
  );

  const visibleSomeSelected = useMemo(
    () => paginatedDocuments.some((d) => selectedIds.includes(d.id)),
    [paginatedDocuments, selectedIds]
  );

  const toggleSelectAllVisible = () => {
    const visibleIds = paginatedDocuments.map((d) => d.id);
    if (visibleAllSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openBulkEdit = () => {
    setBulkEditForm({
      changeCategory: false,
      category: '',
      changeTags: false,
      tags: [],
      tagMode: 'replace',
    });
    setBulkEditTagInput('');
    setBulkEditOpen(true);
  };

  const applyBulkEdit = async () => {
    if (!bulkEditForm.changeCategory && !bulkEditForm.changeTags) {
      toast.warn('Indique o que deseja alterar (categoria e/ou tags).');
      return;
    }
    const tagsFinal = bulkEditForm.changeTags
      ? mergePendingTagInput(bulkEditForm.tags, bulkEditTagInput)
      : null;
    setBulkSaving(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const doc of selectedDocs) {
        const payload = {};
        if (bulkEditForm.changeCategory) {
          payload.category = bulkEditForm.category ? String(bulkEditForm.category).trim() : null;
        }
        if (bulkEditForm.changeTags && tagsFinal) {
          payload.tags =
            bulkEditForm.tagMode === 'append'
              ? [...new Set([...parseDocTags(doc.tags), ...tagsFinal])]
              : tagsFinal;
        }
        try {
          const data = await aiApi.updateDocument(doc.id, payload);
          if (data.success) ok += 1;
          else fail += 1;
        } catch {
          fail += 1;
        }
      }
      if (ok > 0) {
        loadDocuments();
        loadCatalogs();
      }
      if (fail === 0) {
        toast.success(ok === 1 ? 'Documento atualizado.' : `${ok} documentos atualizados.`);
        setBulkEditOpen(false);
        setSelectedIds([]);
      } else if (ok > 0) {
        toast.warning(`${ok} atualizado(s), ${fail} com erro.`);
      } else {
        toast.error('Não foi possível atualizar os documentos selecionados.');
      }
    } finally {
      setBulkSaving(false);
    }
  };

  const runBulkReprocess = async () => {
    setBulkReprocessLoading(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const doc of selectedDocs) {
        try {
          const data = await aiApi.reprocessDocument(doc.id);
          if (data.success) ok += 1;
          else fail += 1;
        } catch {
          fail += 1;
        }
      }
      if (ok > 0) loadDocuments();
      if (fail === 0) {
        toast.success(ok === 1 ? 'Reprocessado.' : `${ok} documentos reprocessados.`);
        setBulkReprocessOpen(false);
        setSelectedIds([]);
      } else if (ok > 0) {
        toast.warning(`${ok} concluído(s), ${fail} com erro.`);
        setBulkReprocessOpen(false);
      } else {
        toast.error('Falha ao reprocessar a seleção.');
      }
    } finally {
      setBulkReprocessLoading(false);
    }
  };

  // Processa os pendentes/parciais em LOTES pequenos (com progresso). Cada lote é um
  // request curto: evita o timeout do PHP num request único e mostra o andamento.
  const runReprocessPending = async () => {
    const ids = pendingDocs.map((d) => d.id);
    if (!ids.length) return;

    const CHUNK = 15;
    setReprocessPendingLoading(true);
    setReprocessPendingProgress({ done: 0, total: ids.length, failed: 0 });

    let done = 0;
    let failed = 0;
    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK);
        try {
          const data = await aiApi.reprocessDocuments(slice);
          if (data?.success) {
            done += data.processed ?? 0;
            failed += data.failed ?? 0;
          } else {
            failed += slice.length;
          }
        } catch {
          failed += slice.length;
        }
        setReprocessPendingProgress({ done: done + failed, total: ids.length, failed });
      }

      if (failed === 0) {
        toast.success(done === 1 ? 'Documento processado.' : `${done} documentos processados.`);
      } else if (done > 0) {
        toast.warning(`${done} concluído(s), ${failed} com erro.`);
      } else {
        toast.error('Falha ao processar os pendentes.');
      }
      setConfirmReprocessPending(false);
      loadDocuments();
    } finally {
      setReprocessPendingLoading(false);
      setReprocessPendingProgress(null);
    }
  };

  const runBulkDelete = async () => {
    setBulkDeleteLoading(true);
    let ok = 0;
    let fail = 0;
    const ids = selectedDocs.map((d) => d.id);
    try {
      for (const id of ids) {
        try {
          const data = await aiApi.deleteDocument(id);
          if (data.success) ok += 1;
          else fail += 1;
        } catch {
          fail += 1;
        }
      }
      if (ok > 0) {
        loadDocuments();
        loadCatalogs();
      }
      if (fail === 0) {
        setSelectedIds([]);
        toast.success(ok === 1 ? 'Documento removido.' : `${ok} documentos removidos.`);
        setBulkDeleteOpen(false);
      } else if (ok > 0) {
        toast.warning(`${ok} removido(s), ${fail} com erro.`);
        setBulkDeleteOpen(false);
      } else {
        toast.error('Não foi possível excluir os documentos selecionados.');
      }
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const runBulkDownload = async () => {
    const downloadable = selectedDocs.filter((d) => d.file_path || d.original_filename);
    if (!downloadable.length) {
      toast.warn('Nenhum documento selecionado tem arquivo para download.');
      return;
    }
    const skipped = selectedDocs.length - downloadable.length;
    setBulkDownloading(true);
    try {
      const { total, skipped: skippedApi } = await aiApi.downloadDocuments(downloadable);
      const extra = skipped + (skippedApi || 0);
      toast.success(
        total === 1
          ? 'Download iniciado.'
          : `${total} downloads iniciados.${extra > 0 ? ` (${extra} sem arquivo)` : ''}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro ao baixar arquivos.');
    } finally {
      setBulkDownloading(false);
    }
  };

  const openCreate = () => {
    setEditMode('create');
    setEditDocId(null);
    setDocTagInput('');
    setForm({
      title: '',
      content: '',
      category: '',
      keywords: '',
      tags: [],
      active: true,
      order: 0,
      scope: null,
    });
    setEditOpen(true);
  };

  const openEdit = (doc) => {
    setEditMode('edit');
    setEditDocId(doc.id);
    setDocTagInput('');
    setForm({
      title: doc.title || '',
      content: doc.content || '',
      category: doc.category || '',
      keywords: doc.keywords != null ? String(doc.keywords) : '',
      tags: parseDocTags(doc.tags),
      active: !!doc.active,
      order: doc.order ?? 0,
      scope: doc.scope ?? null,
    });
    setEditOpen(true);
  };

  const saveDocument = async () => {
    setSaving(true);
    try {
      const tagsFinal = mergePendingTagInput(form.tags, docTagInput);
      const payload = {
        title: form.title,
        content: form.content,
        category: form.category || null,
        keywords: form.keywords ? String(form.keywords).trim() : '',
        tags: tagsFinal,
        active: form.active,
        order: Number(form.order) || 0,
      };
      // So na criacao: a API nao aceita `scope` no update de proposito --
      // documento comum nao vira de plataforma por edicao.
      if (editMode === 'create' && form.scope === 'platform') {
        payload.scope = 'platform';
      }
      if (editMode === 'create') {
        const data = await aiApi.createDocument(payload);
        if (data.success) {
          toast.success('Documento criado.');
          setEditOpen(false);
          setDocTagInput('');
          loadDocuments();
          loadCatalogs();
        } else toast.error(data.error || 'Falha ao criar');
      } else {
        const data = await aiApi.updateDocument(editDocId, payload);
        if (data.success) {
          toast.success('Documento atualizado.');
          setEditOpen(false);
          setDocTagInput('');
          loadDocuments();
          loadCatalogs();
        } else toast.error(data.error || 'Falha ao atualizar');
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const uploadReadyCount = useMemo(
    () => uploadFiles.filter(isUploadReady).length,
    [uploadFiles]
  );

  const uploadDuplicateCount = useMemo(
    () => uploadFiles.filter((e) => e.mode === 'duplicate').length,
    [uploadFiles]
  );

  const checkingDuplicates = useMemo(() => uploadFiles.some((e) => e.checking), [uploadFiles]);

  // Quando não sobrar nenhum duplicado, fecha o painel e volta para o envio.
  useEffect(() => {
    if (manageDuplicatesOpen && uploadDuplicateCount === 0) {
      setManageDuplicatesOpen(false);
    }
  }, [manageDuplicatesOpen, uploadDuplicateCount]);

  const updateUploadEntry = (entryId, patch) => {
    setUploadFiles((prev) => prev.map((e) => (e.id === entryId ? { ...e, ...patch } : e)));
  };

  const runDuplicateCheck = async (entryId, file) => {
    let check = { duplicate: false };
    try {
      const fd = new FormData();
      fd.append('file', file);
      check = await aiApi.checkUploadDocument(fd);
    } catch {
      const local = documents.find(
        (d) =>
          d.original_filename && String(d.original_filename).toLowerCase() === file.name.toLowerCase()
      );
      if (local) {
        check = {
          duplicate: true,
          match_reason: 'filename',
          matches: [
            {
              id: local.id,
              title: local.title,
              original_filename: local.original_filename,
              category: local.category,
              tags: local.tags,
              active: local.active,
              updated_at: local.updated_at,
            },
          ],
          comparison: {
            summary: 'Não foi possível comparar o conteúdo no servidor.',
            identical_content: false,
          },
        };
      } else {
        updateUploadEntry(entryId, { checking: false, checkError: true });
        return;
      }
    }

    if (check.duplicate && check.matches?.length) {
      updateUploadEntry(entryId, {
        checking: false,
        mode: 'duplicate',
        duplicateCheck: check,
      });
    } else {
      updateUploadEntry(entryId, { checking: false, mode: 'new' });
    }
  };

  const openDuplicateDialog = (entry) => {
    if (entry.mode !== 'duplicate' || !entry.duplicateCheck) return;
    setDuplicatePrompt({
      entryId: entry.id,
      file: entry.file,
      check: entry.duplicateCheck,
    });
  };

  const applyDuplicateDecision = (decision, entryIdFromPrompt) => {
    const entryId = entryIdFromPrompt ?? duplicatePrompt?.entryId;
    const check = duplicatePrompt?.check;
    setDuplicatePrompt(null);
    if (!entryId) return;

    if (decision === 'skip') {
      setUploadFiles((prev) => prev.filter((e) => e.id !== entryId));
      return;
    }
    const matchId = check?.matches?.[0]?.id;
    if (decision === 'replace' && matchId) {
      updateUploadEntry(entryId, {
        mode: 'replace',
        replaceDocumentId: matchId,
        duplicateCheck: undefined,
      });
    } else if (decision === 'copy') {
      updateUploadEntry(entryId, { mode: 'copy', duplicateCheck: undefined });
    }
  };

  const applyDuplicateDecisionQuick = (entry, decision) => {
    if (entry.mode !== 'duplicate') return;
    if (decision === 'skip') {
      setUploadFiles((prev) => prev.filter((e) => e.id !== entry.id));
      return;
    }
    const matchId = entry.duplicateCheck?.matches?.[0]?.id;
    if (decision === 'replace' && matchId) {
      updateUploadEntry(entry.id, {
        mode: 'replace',
        replaceDocumentId: matchId,
        duplicateCheck: undefined,
      });
    } else if (decision === 'copy') {
      updateUploadEntry(entry.id, { mode: 'copy', duplicateCheck: undefined });
    }
  };

  // Aplica uma decisão a TODOS os duplicados de uma vez (substituir / cópia / remover).
  const applyDuplicateDecisionAll = (decision) => {
    if (decision === 'skip') {
      setUploadFiles((prev) => prev.filter((e) => e.mode !== 'duplicate'));
      return;
    }
    setUploadFiles((prev) =>
      prev.map((e) => {
        if (e.mode !== 'duplicate') return e;
        if (decision === 'replace') {
          const matchId = e.duplicateCheck?.matches?.[0]?.id;
          return matchId
            ? { ...e, mode: 'replace', replaceDocumentId: matchId, duplicateCheck: undefined }
            : e;
        }
        if (decision === 'copy') {
          return { ...e, mode: 'copy', duplicateCheck: undefined };
        }
        return e;
      })
    );
  };

  const addUploadFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const rejected = [];
    const accepted = [];
    incoming.forEach((file) => {
      const err = validateUploadFile(file);
      if (err) rejected.push({ name: file.name, err });
      else accepted.push(file);
    });
    if (rejected.length) {
      toast.warn(
        `${rejected.length} arquivo(s) ignorado(s): ${rejected
          .slice(0, 3)
          .map((r) => r.name)
          .join(', ')}${rejected.length > 3 ? '…' : ''}`
      );
    }
    if (!accepted.length) return;

    const existingKeys = new Set(uploadFiles.map((e) => `${e.file.name}-${e.file.size}`));
    const toAdd = [];
    accepted.forEach((file) => {
      const batchKey = `${file.name}-${file.size}`;
      if (existingKeys.has(batchKey)) return;
      existingKeys.add(batchKey);
      const id = newUploadEntryId();
      toAdd.push({ id, file, mode: 'new', checking: true });
    });

    if (!toAdd.length) return;

    setUploadFiles((prev) => [...prev, ...toAdd]);
    // Roda as verificações de duplicado em FILA com concorrência limitada.
    // Disparar todas de uma vez (ex.: centenas de arquivos) sobrecarrega o servidor
    // e estoura o timeout -> "Verificação indisponível".
    runDuplicateChecksQueued(toAdd, 3);
  };

  const runDuplicateChecksQueued = async (entries, concurrency = 3) => {
    const queue = [...entries];
    const worker = async () => {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const entry = queue.shift();
        if (!entry) return;
        try {
          await runDuplicateCheck(entry.id, entry.file);
        } catch {
          /* runDuplicateCheck já trata os próprios erros */
        }
      }
    };
    const n = Math.min(Math.max(1, concurrency), queue.length);
    await Promise.all(Array.from({ length: n }, () => worker()));
  };

  const removeUploadFile = (index) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const closeUploadDialog = () => {
    if (uploading) return;
    setUploadOpen(false);
    setUploadFiles([]);
    setUploadQueue(null);
    setDuplicatePrompt(null);
    setUploadMeta({ category: '', tags: '', active: true });
  };

  const handleUpload = async () => {
    const toUpload = uploadFiles.filter(isUploadReady);
    if (!toUpload.length) {
      if (uploadDuplicateCount > 0) {
        toast.warn('Resolva os duplicados na fila (substituir, cópia ou ignorar) antes de enviar.');
      } else if (checkingDuplicates) {
        toast.info('Aguarde a verificação dos arquivos na fila.');
      } else {
        toast.error('Selecione pelo menos um arquivo.');
      }
      return;
    }
    if (uploadDuplicateCount > 0) {
      toast.info(
        `${uploadDuplicateCount} duplicado(s) ficam na fila e não serão enviados até resolver.`
      );
    }
    const queue = toUpload.map((entry) => ({ name: entry.file.name, status: 'pending' }));
    setUploadQueue(queue);
    setUploading(true);
    let ok = 0;
    let fail = 0;
    const remainingFiles = [];
    try {
      for (let i = 0; i < toUpload.length; i++) {
        const entry = toUpload[i];
        const { file } = entry;
        setUploadQueue((prev) =>
          prev.map((item, idx) => (idx === i ? { ...item, status: 'uploading' } : item))
        );
        try {
          const fd = new FormData();
          fd.append('file', file);
          if (uploadMeta.category) fd.append('category', uploadMeta.category);
          fd.append('active', uploadMeta.active ? 'true' : 'false');
          if (uploadMeta.tags) fd.append('tags', uploadMeta.tags);
          if (entry.mode === 'replace' && entry.replaceDocumentId) {
            fd.append('replace_document_id', String(entry.replaceDocumentId));
          }
          if (entry.mode === 'copy') {
            fd.append('as_copy', 'true');
          }
          const data = await aiApi.uploadDocument(fd);
          if (data.success) {
            ok += 1;
            queue[i] = { name: file.name, status: 'done', message: data.message || 'OK' };
          } else {
            fail += 1;
            const msg = data.error || 'Falha no upload';
            queue[i] = { name: file.name, status: 'error', message: msg };
            remainingFiles.push(entry);
          }
        } catch (e) {
          fail += 1;
          const msg = e?.response?.data?.error || e?.errorMessage || 'Erro no upload';
          queue[i] = { name: file.name, status: 'error', message: msg };
          remainingFiles.push(entry);
        }
        setUploadQueue([...queue]);
      }
      if (ok > 0) {
        loadDocuments();
        loadCatalogs();
      }
      const unresolved = uploadFiles.filter((e) => e.mode === 'duplicate' || e.checking);
      if (fail === 0) {
        toast.success(ok === 1 ? 'Upload concluído.' : `${ok} arquivos importados com sucesso.`);
        if (unresolved.length) {
          setUploadFiles(unresolved);
          setUploadQueue(null);
        } else {
          closeUploadDialog();
        }
      } else if (ok > 0) {
        toast.warning(`${ok} concluído(s), ${fail} com erro. Você pode reenviar os que falharam.`);
        setUploadFiles([...remainingFiles, ...unresolved]);
        setUploadQueue(queue.filter((q) => q.status === 'error'));
      } else {
        toast.error('Nenhum arquivo foi importado.');
        setUploadFiles([...remainingFiles, ...unresolved]);
        setUploadQueue(queue.filter((q) => q.status === 'error'));
      }
    } finally {
      setUploading(false);
    }
  };

  const uploadProgress = useMemo(() => {
    if (!uploadQueue?.length) return null;
    const done = uploadQueue.filter((q) => q.status === 'done' || q.status === 'error').length;
    return { done, total: uploadQueue.length };
  }, [uploadQueue]);

  const openDetail = async (doc) => {
    setDetailDoc(doc);
    setDetailOpen(true);
    setDetailStatus(null);
    setDetailLoading(true);
    try {
      const s = await aiApi.getIndexStatus(doc.id);
      setDetailStatus(s);
    } catch {
      setDetailStatus({ success: false, error: 'Erro ao carregar status' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleReprocess = async (id) => {
    setReprocessId(id);
    try {
      const data = await aiApi.reprocessDocument(id);
      if (data.success) {
        toast.success(data.message || 'Reprocessamento concluído.');
        loadDocuments();
        if (detailOpen && detailDoc?.id === id) {
          try {
            const s = await aiApi.getIndexStatus(id);
            setDetailStatus(s);
          } catch {}
        }
      } else toast.error(data.error || 'Falha ao reprocessar');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro ao reprocessar');
    } finally {
      setReprocessId(null);
    }
  };

  const handleToggleActive = async (doc) => {
    try {
      const data = await aiApi.updateDocument(doc.id, { active: !doc.active });
      if (data.success) {
        toast.success(doc.active ? 'Documento desativado.' : 'Documento ativado.');
        loadDocuments();
      } else toast.error(data.error || 'Falha');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro');
    }
  };

  const handleDownload = async (doc) => {
    try {
      await aiApi.downloadDocument(doc);
      toast.success('Download iniciado.');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro no download');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const data = await aiApi.deleteDocument(deleteTarget.id);
      if (data.success) {
        toast.success('Documento removido.');
        setDeleteTarget(null);
        loadDocuments();
        loadCatalogs();
      } else toast.error(data.error || 'Falha ao excluir');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro ao excluir');
    }
  };

  const runReprocessAll = async () => {
    setReprocessAllLoading(true);
    try {
      const data = await aiApi.reprocessAll();
      if (data.success) {
        toast.success(`Processados: ${data.processed}/${data.total}. Falhas: ${data.failed}.`);
        setConfirmReprocessAll(false);
        loadDocuments();
      } else toast.error(data.error || 'Falha');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro (apenas admin).');
    } finally {
      setReprocessAllLoading(false);
    }
  };

  const indexLabel = (docId) => {
    if (loadingIndexBatch && !indexByDoc[docId]) return '…';
    const s = indexByDoc[docId];
    if (!s?.success) return '—';
    if (!s.has_content) return 'Sem conteúdo';
    if (s.chunks_count > 0 && s.has_qdrant_points) return 'Indexado';
    if (s.chunks_count > 0) return 'Parcial';
    if (s.has_content) return 'Pendente';
    return '—';
  };

  const [sq, setSq] = useState('');
  const [sqLimit, setSqLimit] = useState(5);
  const [sqLoading, setSqLoading] = useState(false);
  const [sqResults, setSqResults] = useState(null);

  const runSemanticSearch = async () => {
    if (!sq.trim()) {
      toast.error('Digite uma pergunta.');
      return;
    }
    setSqLoading(true);
    try {
      const data = await aiApi.semanticSearch(sq.trim(), sqLimit);
      if (data.success) setSqResults(data);
      else toast.error(data.error || 'Erro na busca');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro na busca');
    } finally {
      setSqLoading(false);
    }
  };

  const [chatMsg, setChatMsg] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatResult, setChatResult] = useState(null);

  const runChat = async () => {
    if (!chatMsg.trim()) {
      toast.error('Digite uma mensagem.');
      return;
    }
    setChatLoading(true);
    setChatResult(null);
    try {
      const data = await aiApi.chatTest(chatMsg.trim());
      if (data.success) setChatResult(data);
      else toast.error(data.error || 'Erro no chat');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro no chat');
    } finally {
      setChatLoading(false);
    }
  };

  const actionBtnSx = { textTransform: 'none', fontWeight: 500, borderRadius: 2 };

  const docCountLabel = useMemo(() => {
    if (loadingDocs) return 'Carregando documentos…';
    const n = documents.length;
    return `${n} ${n === 1 ? 'documento' : 'documentos'} no momento`;
  }, [documents.length, loadingDocs]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          padding: '1rem 1.5rem',
          flexWrap: 'wrap',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} sx={{ color: theme.palette.text.primary, lineHeight: 1.2 }}>
            IA / Base de Conhecimento
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            {tab === 0
              ? docCountLabel
              : tab === 1
                ? 'Teste de busca semântica na base de conhecimento'
                : 'Teste do assistente com contexto RAG'}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: '1.5rem',
          pb: '1.5rem',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 2,
              flexShrink: 0,
              borderBottom: 1,
              borderColor: 'divider',
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                minHeight: 44,
              },
            }}
          >
            <Tab label="Documentos" />
            <Tab label="Busca semântica" />
            <Tab label="Teste do chat IA" />
            {canReviewFeedback && <Tab label="Análises" />}
            {/* Consumo de IA (ponto 16): mesma audiência das Análises, então
                entra sob a MESMA permissão -- sem rota nova, sem permissão
                nova e sem backfill no banco, que é o custo escondido de tela
                nova neste projeto. */}
            {canReviewFeedback && <Tab label="Consumo de IA" />}
          </Tabs>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: tab === 0 ? 'hidden' : 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
        {tab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Stack spacing={1.5} sx={{ flexShrink: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <FormControlLabel
                sx={{
                  ml: 0,
                  mr: 0,
                  pl: 0,
                  alignItems: 'center',
                  gap: 0.5,
                }}
                control={
                  <Switch
                    size="small"
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                    color="primary"
                    sx={{ ml: 0 }}
                  />
                }
                label={
                  <Typography variant="body2" component="span">
                    Incluir documentos inativos
                  </Typography>
                }
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={openCreate}
                  sx={{
                    ...actionBtnSx,
                    ...(theme.palette.mode === 'light' && {
                      '& .MuiButton-startIcon .MuiSvgIcon-root': {
                        color: `${theme.palette.primary.contrastText} !important`,
                      },
                    }),
                  }}
                >
                  Novo documento
                </Button>
                <Button size="small" variant="outlined" color="primary" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)} sx={actionBtnSx}>
                  Upload
                </Button>
                {isAdmin && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<RefreshIcon />}
                    onClick={() => setConfirmReprocessPending(true)}
                    disabled={pendingDocs.length === 0}
                    sx={actionBtnSx}
                  >
                    Processar pendentes ({pendingDocs.length})
                  </Button>
                )}
                {isAdmin && (
                  <Button size="small" variant="outlined" color="warning" startIcon={<RefreshIcon />} onClick={() => setConfirmReprocessAll(true)} sx={actionBtnSx}>
                    Reprocessar todos
                  </Button>
                )}
                <Button size="small" variant="outlined" onClick={loadDocuments} disabled={loadingDocs} sx={actionBtnSx}>
                  Atualizar
                </Button>
              </Box>
            </Box>

            {documents.length > 0 && (
              <Box>
                <Divider sx={{ mb: 1.5 }} />
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', md: 'flex-end' }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <TextField
                    size="small"
                    label="Pesquisar"
                    placeholder="Nome, arquivo ou ID…"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    sx={{
                      flex: { md: '1 1 200px' },
                      minWidth: { xs: '100%', md: 160 },
                      maxWidth: { md: 280 },
                      ...KB_FILTER_FIELD_SX,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {mergedTagFilterOptions.length > 0 && (
                    <FormControl
                      size="small"
                      sx={{
                        flex: { md: '1 1 180px' },
                        minWidth: { xs: '100%', md: 160 },
                        maxWidth: { md: 240 },
                        ...KB_FILTER_FIELD_SX,
                      }}
                    >
                      <InputLabel id="kb-filter-tags" shrink>
                        Tags
                      </InputLabel>
                      <Select
                        multiple
                        displayEmpty
                        labelId="kb-filter-tags"
                        label="Tags"
                        value={selectedFilterTags}
                        onChange={(e) => {
                          const v = e.target.value;
                          const next = typeof v === 'string' ? v.split(',') : v;
                          setSelectedFilterTags(
                            [...new Set(next.map((t) => String(t).trim().toLowerCase()).filter(Boolean))].sort((a, b) =>
                              a.localeCompare(b)
                            )
                          );
                        }}
                        renderValue={(selected) => {
                          if (!selected.length) {
                            return (
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                sx={{ lineHeight: 1.43 }}
                              >
                                Todas
                              </Typography>
                            );
                          }
                          const text = selected.join(', ');
                          if (selected.length > 2) {
                            return (
                              <Typography component="span" variant="body2" noWrap>
                                {selected.slice(0, 2).join(', ')} +{selected.length - 2}
                              </Typography>
                            );
                          }
                          return (
                            <Typography component="span" variant="body2" noWrap>
                              {text}
                            </Typography>
                          );
                        }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
                      >
                        {mergedTagFilterOptions.map((t) => (
                          <MenuItem key={t} value={t} dense>
                            <Checkbox size="small" checked={selectedFilterTags.includes(t)} sx={{ py: 0, mr: 1 }} />
                            <ListItemText primary={t} primaryTypographyProps={{ variant: 'body2' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <FormControl
                    size="small"
                    sx={{
                      flex: { md: '1 1 160px' },
                      minWidth: { xs: '100%', md: 160 },
                      maxWidth: { md: 240 },
                      ...KB_FILTER_FIELD_SX,
                    }}
                  >
                    <InputLabel id="kb-filter-cat" shrink>
                      Categoria
                    </InputLabel>
                    <Select
                      displayEmpty
                      labelId="kb-filter-cat"
                      label="Categoria"
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      renderValue={(value) => {
                        if (!value) {
                          return (
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              sx={{ lineHeight: 1.43 }}
                            >
                              Todas
                            </Typography>
                          );
                        }
                        return (
                          <Typography component="span" variant="body2" noWrap>
                            {value}
                          </Typography>
                        );
                      }}
                    >
                      <MenuItem value="">
                        Todas
                      </MenuItem>
                      {mergedCategoryFilterOptions.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl
                    size="small"
                    sx={{
                      flex: { md: '0 0 auto' },
                      minWidth: { xs: '100%', md: 200 },
                      maxWidth: { md: 240 },
                      ...KB_FILTER_FIELD_SX,
                    }}
                  >
                    <InputLabel id="kb-sort-by" shrink>
                      Ordenar
                    </InputLabel>
                    <Select
                      labelId="kb-sort-by"
                      label="Ordenar"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <MenuItem value="newest">Mais recentes (último envio)</MenuItem>
                      <MenuItem value="oldest">Mais antigos</MenuItem>
                      <MenuItem value="id_desc">ID (maior → menor)</MenuItem>
                      <MenuItem value="id_asc">ID (menor → maior)</MenuItem>
                    </Select>
                  </FormControl>
                  {hasListFilters && (
                    <Button size="small" onClick={clearListFilters} sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}>
                      Limpar filtros
                    </Button>
                  )}
                </Stack>
                {hasListFilters && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {sortedDocuments.length} de {documents.length} documento(s)
                  </Typography>
                )}
              </Box>
            )}

            {hasListFilters && documents.length > 0 && sortedDocuments.length === 0 && (
              <Alert severity="info">Nenhum documento corresponde à pesquisa ou aos filtros.</Alert>
            )}

            {selectedIds.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1.25,
                  borderRadius: `${theme.shape.borderRadius}px`,
                  bgcolor: 'action.selected',
                  borderColor: 'primary.main',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedIds.length} selecionado(s)
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setBulkActionsAnchor(e.currentTarget)}
                    disabled={bulkReprocessLoading || bulkDownloading || bulkDeleteLoading}
                  >
                    Ações
                  </Button>
                  <Button size="small" variant="text" onClick={() => setSelectedIds([])}>
                    Limpar seleção
                  </Button>
                </Stack>
                <Menu
                  anchorEl={bulkActionsAnchor}
                  open={Boolean(bulkActionsAnchor)}
                  onClose={() => setBulkActionsAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  PaperProps={{ sx: { minWidth: 220 } }}
                >
                  <MenuItem
                    onClick={() => {
                      setBulkActionsAnchor(null);
                      openBulkEdit();
                    }}
                  >
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Editar categoria / tags" />
                  </MenuItem>
                  <MenuItem
                    disabled={bulkReprocessLoading}
                    onClick={() => {
                      setBulkActionsAnchor(null);
                      setBulkReprocessOpen(true);
                    }}
                  >
                    <ListItemIcon>
                      <RefreshIcon fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText primary="Reprocessar" />
                  </MenuItem>
                  <MenuItem
                    disabled={bulkDownloading}
                    onClick={() => {
                      setBulkActionsAnchor(null);
                      runBulkDownload();
                    }}
                  >
                    <ListItemIcon>
                      <GetAppIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={bulkDownloading ? 'Baixando…' : 'Download'} />
                  </MenuItem>
                  {/* A exclusão em lote segue a mesma regra da individual:
                      só administrador (ver o comentário no menu da linha). */}
                  {isAdmin && <Divider />}
                  {isAdmin && (
                  <MenuItem
                    disabled={bulkDeleteLoading}
                    onClick={() => {
                      setBulkActionsAnchor(null);
                      setBulkDeleteOpen(true);
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText primary="Excluir" />
                  </MenuItem>
                  )}
                </Menu>
              </Paper>
            )}
          </Stack>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                mt: 1.5,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
            {loadingDocs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  overflow: 'auto',
                  bgcolor: 'background.paper',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>
                        <Checkbox
                          size="small"
                          checked={visibleAllSelected}
                          indeterminate={visibleSomeSelected && !visibleAllSelected}
                          onChange={toggleSelectAllVisible}
                          disabled={!paginatedDocuments.length}
                          inputProps={{ 'aria-label': 'Selecionar todos desta página' }}
                        />
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>ID</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Título</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Categoria</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Tags</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Ativo</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Empresa</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Chunks</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Indexação</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Embedding</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Último processamento</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDocuments.map((doc) => {
                      const idx = indexByDoc[doc.id];
                      return (
                        <TableRow
                          key={doc.id}
                          hover
                          selected={selectedIds.includes(doc.id)}
                          sx={selectedIds.includes(doc.id) ? { bgcolor: 'action.selected' } : undefined}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selectedIds.includes(doc.id)}
                              onChange={() => toggleSelectRow(doc.id)}
                              inputProps={{ 'aria-label': `Selecionar documento ${doc.id}` }}
                            />
                          </TableCell>
                          <TableCell>{doc.id}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>{doc.title}</TableCell>
                          <TableCell>{doc.category || '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 160 }}>{formatTags(doc.tags)}</TableCell>
                          <TableCell>{doc.active ? 'Sim' : 'Não'}</TableCell>
                          <TableCell>
                            {doc.company_id != null ? doc.company_id : '—'}
                            {doc.scope === 'platform' ? (
                              <Chip size="small" color="primary" label="Plataforma" sx={{ ml: 0.5 }} variant="outlined" />
                            ) : doc.scope === 'tenant' || doc.is_global ? (
                              <Chip size="small" label="Global" sx={{ ml: 0.5 }} variant="outlined" />
                            ) : null}
                          </TableCell>
                          <TableCell align="right">{idx?.chunks_count ?? '—'}</TableCell>
                          <TableCell>{indexLabel(doc.id)}</TableCell>
                          <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {idx?.embedding_model ?? '—'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {idx?.last_processed_at
                              ? new Date(idx.last_processed_at).toLocaleString('pt-BR')
                              : '—'}
                          </TableCell>
                          <TableCell align="right" padding="checkbox">
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={`Ações do documento ${doc.id}`}
                              onClick={(e) => openRowMenu(e, doc)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!sortedDocuments.length && (
                      <TableRow>
                        <TableCell colSpan={12}>
                          <Typography color="text.secondary">Nenhum documento encontrado.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            </Box>

            {!loadingDocs && sortedDocuments.length > 0 && (
              <TablePagination
                component="div"
                count={sortedDocuments.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                labelRowsPerPage="Por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
                sx={{
                  flexShrink: 0,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'background.paper',
                  '.MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 0.5 },
                }}
              />
            )}

            <Menu
              anchorEl={rowMenuAnchor}
              open={Boolean(rowMenuAnchor)}
              onClose={closeRowMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { minWidth: 200 } }}
            >
              {rowMenuDoc && (rowMenuDoc.file_path || rowMenuDoc.original_filename) && (
                <MenuItem
                  onClick={() => {
                    handleDownload(rowMenuDoc);
                    closeRowMenu();
                  }}
                >
                  <ListItemIcon>
                    <GetAppIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Download" />
                </MenuItem>
              )}
              {rowMenuDoc && (
                <MenuItem
                  onClick={() => {
                    openDetail(rowMenuDoc);
                    closeRowMenu();
                  }}
                >
                  <ListItemIcon>
                    <VisibilityIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Detalhes / status" />
                </MenuItem>
              )}
              {rowMenuDoc && (
                <MenuItem
                  disabled={reprocessId === rowMenuDoc.id}
                  onClick={() => {
                    handleReprocess(rowMenuDoc.id);
                    closeRowMenu();
                  }}
                >
                  <ListItemIcon>
                    {reprocessId === rowMenuDoc.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <RefreshIcon fontSize="small" color="warning" />
                    )}
                  </ListItemIcon>
                  <ListItemText primary="Reprocessar" />
                </MenuItem>
              )}
              {rowMenuDoc && (
                <MenuItem
                  onClick={() => {
                    openEdit(rowMenuDoc);
                    closeRowMenu();
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Editar" />
                </MenuItem>
              )}
              {rowMenuDoc && (
                <MenuItem
                  onClick={() => {
                    handleToggleActive(rowMenuDoc);
                    closeRowMenu();
                  }}
                >
                  <ListItemIcon>
                    {rowMenuDoc.active ? (
                      <ToggleOffIcon fontSize="small" />
                    ) : (
                      <ToggleOnIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText primary={rowMenuDoc.active ? 'Desativar' : 'Ativar'} />
                </MenuItem>
              )}
              {rowMenuDoc && isAdmin && <Divider />}
              {/*
                * "Excluir" passou a ser só de administrador (Gisele, 23/10/2026).
                *
                * O pedido veio na tela de vídeos, mas vale para a capacidade:
                * apagar tira o material da base E do índice do Orby, para todo
                * mundo, sem desfazer. A rota `DELETE /ai-documents/{id}` ganhou
                * o middleware `admin` no mesmo commit -- este `isAdmin` existe
                * para o operador não ver um botão que responderia 403, e não
                * como a trava em si.
                *
                * "Desativar" continua para todos: é reversível, e é a saída de
                * quem precisa tirar algo do ar sem apagar.
                */}
              {rowMenuDoc && isAdmin && (
                <MenuItem
                  onClick={() => {
                    setDeleteTarget(rowMenuDoc);
                    closeRowMenu();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Excluir" />
                </MenuItem>
              )}
            </Menu>
          </Box>
        )}

        {tab === 1 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Testa a mesma API que o operador usa internamente (<code>POST /api/ai-search</code>). Scores abaixo de 0,55 tendem a ser filtrados no chat.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
              <TextField
                fullWidth
                label="Digite uma pergunta para testar a base"
                value={sq}
                onChange={(e) => setSq(e.target.value)}
                multiline
                minRows={2}
              />
              <TextField
                label="Limite"
                type="number"
                value={sqLimit}
                onChange={(e) => setSqLimit(Math.min(20, Math.max(1, Number(e.target.value) || 5)))}
                sx={{ width: 100 }}
              />
              <Button size="small" variant="contained" onClick={runSemanticSearch} disabled={sqLoading} startIcon={sqLoading ? <CircularProgress size={18} /> : <SearchIcon />} sx={actionBtnSx}>
                Buscar
              </Button>
            </Stack>
            {sqResults?.success && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={600}>Resultados ({sqResults.count})</Typography>
                {(sqResults.results || []).map((r, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                      <Chip size="small" {...scoreChipProps(r.score)} />
                      <Typography variant="subtitle2">{r.document?.title || '—'}</Typography>
                      <Chip size="small" label={r.document?.category || '—'} variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        chunk #{r.chunk?.index ?? '—'}
                      </Typography>
                      {r.is_global && <Chip size="small" label="Global" />}
                      {r.company_id != null && <Chip size="small" label={`Empresa ${r.company_id}`} variant="outlined" />}
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {r.content}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        )}

        {tab === 2 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Chamada opcional a <code>POST /api/ai-assistant/chat</code> com o mesmo JWT. Mostra resposta e metadados RAG.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
              <TextField
                fullWidth
                label="Mensagem"
                value={chatMsg}
                onChange={(e) => setChatMsg(e.target.value)}
                multiline
                minRows={3}
              />
              <Button size="small" variant="contained" onClick={runChat} disabled={chatLoading} startIcon={chatLoading ? <CircularProgress size={18} /> : <SmartToyIcon />} sx={actionBtnSx}>
                Enviar
              </Button>
            </Stack>
            {chatResult && (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Resposta
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                  {chatResult.message}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" component="div" color="text.secondary">
                  rag.used: {String(chatResult.rag?.used)}
                  {' · '}
                  fallback_used: {String(chatResult.rag?.fallback_used)}
                  {chatResult.rag?.fallback_reason != null && ` (${chatResult.rag.fallback_reason})`}
                </Typography>
                {Array.isArray(chatResult.rag?.sources) && chatResult.rag.sources.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" fontWeight={600}>
                      Fontes RAG
                    </Typography>
                    <ul style={{ margin: '4px 0 0 16px' }}>
                      {chatResult.rag.sources.map((s, i) => (
                        <li key={i}>
                          <Typography variant="caption">
                            {s.title} — score {s.score}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Paper>
            )}
          </Stack>
        )}
        {tab === 3 && canReviewFeedback && (
          <FeedbackAnalytics />
        )}
        {tab === 4 && canReviewFeedback && (
          <ConsumoDeIA />
        )}
          </Box>
        </Paper>
      </Box>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Documento #{detailDoc?.id}</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <CircularProgress />
          ) : detailStatus?.success ? (
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Título:</strong> {detailDoc?.title}
              </Typography>
              <Typography variant="body2">
                <strong>has_content:</strong> {String(detailStatus.has_content)}
              </Typography>
              <Typography variant="body2">
                <strong>content_length:</strong> {detailStatus.content_length}
              </Typography>
              <Typography variant="body2">
                <strong>chunks_count:</strong> {detailStatus.chunks_count}
              </Typography>
              <Typography variant="body2">
                <strong>has_qdrant_points:</strong> {String(detailStatus.has_qdrant_points)} ({detailStatus.qdrant_points_count ?? 0} pontos)
              </Typography>
              <Typography variant="body2">
                <strong>last_processed_at:</strong> {detailStatus.last_processed_at || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>embedding_model:</strong> {detailStatus.embedding_model || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>company_id:</strong> {detailStatus.company_id ?? '—'}
              </Typography>
              <Typography variant="body2">
                <strong>is_global:</strong> {String(detailStatus.is_global)}
              </Typography>
            </Stack>
          ) : (
            <Alert severity="error">Não foi possível carregar o status.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Fechar</Button>
          {detailDoc && (
            <Button
              variant="contained"
              onClick={() => handleReprocess(detailDoc.id)}
              disabled={reprocessId === detailDoc.id}
              startIcon={reprocessId === detailDoc.id ? <CircularProgress size={18} /> : <RefreshIcon />}
            >
              Reprocessar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setDocTagInput('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editMode === 'create' ? 'Novo documento' : `Editar documento #${editDocId}`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Título" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required fullWidth />
            <TextField
              label="Categoria"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              fullWidth
              placeholder="Ou clique numa sugestão abaixo"
            />
            {categoryOptionsForForm.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                  Sugestões
                </Typography>
                {categoryOptionsForForm.slice(0, 16).map((c) => (
                  <Chip key={c} size="small" label={c} variant="outlined" onClick={() => setForm((f) => ({ ...f, category: c }))} sx={{ cursor: 'pointer' }} />
                ))}
              </Box>
            )}
            <Autocomplete
              multiple
              freeSolo
              options={tagOptionsForForm}
              value={form.tags}
              inputValue={docTagInput}
              onInputChange={(_, newInput, reason) => {
                if (reason === 'input' || reason === 'clear') setDocTagInput(newInput);
              }}
              onChange={(_, newValue) => {
                const cleaned = newValue.map((t) => String(t).trim().toLowerCase()).filter(Boolean);
                setForm((f) => ({ ...f, tags: [...new Set(cleaned)] }));
                setDocTagInput('');
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => <Chip variant="outlined" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />)
              }
              renderInput={(params) => (
                <TextField {...params} label="Tags" placeholder="Enter para confirmar" helperText="Tags usadas pelo RAG e pelas regras de configuração." />
              )}
            />
            <TextField
              label="Palavras-chave"
              value={form.keywords}
              onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))}
              fullWidth
              placeholder="Separadas por vírgula (opcional)"
              helperText="Metadados opcionais para pesquisa; não substituem o conteúdo."
            />
            <TextField
              label="Ordem"
              type="number"
              value={form.order}
              onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
              sx={{ maxWidth: 120 }}
            />
            <FormControlLabel
              control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />}
              label="Ativo"
            />
            {isSuperAdmin && editMode === 'create' && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.scope === 'platform'}
                      onChange={(e) => setForm((f) => ({ ...f, scope: e.target.checked ? 'platform' : null }))}
                    />
                  }
                  label="Publicar para toda a plataforma"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                  O documento passa a ser usado nas respostas de todos os clientes, não só desta empresa.
                </Typography>
              </Box>
            )}
            {editMode === 'edit' && form.scope === 'platform' && (
              <Typography variant="caption" color="text.secondary">
                Documento publicado para toda a plataforma. O alcance não muda por edição.
              </Typography>
            )}
            <TextField
              label="Conteúdo"
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              multiline
              minRows={10}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditOpen(false);
              setDocTagInput('');
            }}
          >
            Cancelar
          </Button>
          <Button variant="contained" onClick={saveDocument} disabled={saving || !form.title || !form.content}>
            {saving ? <CircularProgress size={22} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={uploadOpen} onClose={closeUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {uploading && uploadProgress
            ? `A enviar ${uploadProgress.done} de ${uploadProgress.total}…`
            : 'Upload de documentos'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {uploading && uploadProgress && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={(uploadProgress.done / uploadProgress.total) * 100}
                  sx={{ mb: 1, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Os arquivos são processados um a um (indexação no servidor). Você pode fechar esta janela após concluir.
                </Typography>
              </Box>
            )}
            {uploadQueue?.length > 0 && (
              <List dense disablePadding sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1 }}>
                {uploadQueue.map((item, idx) => (
                  <ListItem key={`${item.name}-${idx}`} disableGutters sx={{ px: 1 }}>
                    <ListItemText
                      primary={item.name}
                      secondary={
                        item.status === 'uploading'
                          ? 'A enviar…'
                          : item.status === 'done'
                            ? 'Concluído'
                            : item.status === 'error'
                              ? item.message || 'Erro'
                              : 'Na fila'
                      }
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: item.status === 'error' ? 'error' : 'text.secondary',
                      }}
                    />
                    {item.status === 'uploading' && <CircularProgress size={18} sx={{ ml: 1 }} />}
                    {item.status === 'done' && (
                      <Chip label="OK" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                    )}
                  </ListItem>
                ))}
              </List>
            )}
            <Button variant="outlined" component="label" disabled={uploading}>
              {uploadFiles.length ? 'Adicionar mais arquivos' : 'Escolher arquivos'}
              <input
                type="file"
                hidden
                multiple
                accept={UPLOAD_ACCEPT}
                disabled={uploading}
                onChange={(e) => {
                  addUploadFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </Button>
            {uploadDuplicateCount > 0 && !uploading && (
              <Alert
                severity="warning"
                sx={{ py: 0.5, cursor: 'pointer' }}
                onClick={() => setManageDuplicatesOpen(true)}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setManageDuplicatesOpen(true); }}
                  >
                    Gerenciar
                  </Button>
                }
              >
                {uploadDuplicateCount} arquivo(s) duplicado(s) na fila — clique aqui para gerenciar todos de uma vez (ou um a um).
              </Alert>
            )}
            {uploadFiles.length > 0 && !uploadQueue?.length && (
              <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto' }}>
                {uploadFiles.map((entry, idx) => (
                  <ListItem
                    key={uploadEntryKey(entry)}
                    disableGutters
                    sx={{
                      px: 0.5,
                      borderRadius: 1,
                      bgcolor: entry.mode === 'duplicate' ? 'warning.light' : 'transparent',
                      opacity: entry.mode === 'duplicate' ? 1 : undefined,
                      ...(entry.mode === 'duplicate' && {
                        bgcolor: (t) =>
                          t.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.12)' : 'rgba(237, 108, 2, 0.08)',
                      }),
                    }}
                    secondaryAction={
                      !uploading ? (
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                          {entry.mode === 'duplicate' && (
                            <>
                              <Tooltip title="Substituir existente">
                                <Button
                                  size="small"
                                  variant="text"
                                  sx={{ minWidth: 0, px: 0.75 }}
                                  onClick={() => applyDuplicateDecisionQuick(entry, 'replace')}
                                >
                                  Subst.
                                </Button>
                              </Tooltip>
                              <Tooltip title="Nova cópia">
                                <Button
                                  size="small"
                                  variant="text"
                                  sx={{ minWidth: 0, px: 0.75 }}
                                  onClick={() => applyDuplicateDecisionQuick(entry, 'copy')}
                                >
                                  Cópia
                                </Button>
                              </Tooltip>
                            </>
                          )}
                          <IconButton
                            edge="end"
                            size="small"
                            aria-label="remover"
                            onClick={() => removeUploadFile(idx)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : null
                    }
                  >
                    {entry.checking && (
                      <CircularProgress size={16} sx={{ mr: 1, flexShrink: 0 }} />
                    )}
                    {entry.mode === 'duplicate' && !entry.checking && (
                      <Tooltip title="Ver diferenças e decidir">
                        <IconButton
                          size="small"
                          color="warning"
                          aria-label="duplicado — ver diferenças"
                          onClick={() => openDuplicateDialog(entry)}
                          sx={{ mr: 0.5, flexShrink: 0 }}
                        >
                          <WarningAmberIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <ListItemText
                      primary={entry.file.name}
                      secondary={
                        entry.checking
                          ? 'A verificar duplicado…'
                          : entry.mode === 'duplicate'
                            ? 'Duplicado — clique no aviso para comparar'
                            : entry.checkError
                              ? 'Verificação indisponível'
                              : `${formatBytes(entry.file.size)}${
                                  entry.mode === 'replace' && entry.replaceDocumentId
                                    ? ` · substituir #${entry.replaceDocumentId}`
                                    : ''
                                }${entry.mode === 'copy' ? ' · nova cópia' : ''}`
                      }
                      primaryTypographyProps={{
                        variant: 'body2',
                        noWrap: true,
                        sx: { cursor: entry.mode === 'duplicate' ? 'pointer' : 'default' },
                        onClick: entry.mode === 'duplicate' ? () => openDuplicateDialog(entry) : undefined,
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: entry.mode === 'duplicate' ? 'warning.dark' : 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            <Typography variant="caption" color="text.secondary">
              PDF, TXT, DOC ou DOCX — até 10 MB cada. Categoria e tags se aplicam a todos os arquivos deste envio.
            </Typography>
            <TextField
              label="Categoria"
              value={uploadMeta.category}
              onChange={(e) => setUploadMeta((m) => ({ ...m, category: e.target.value }))}
              fullWidth
              disabled={uploading}
            />
            {mergedCategoryFilterOptions.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {mergedCategoryFilterOptions.slice(0, 12).map((c) => (
                  <Chip key={c} size="small" label={c} variant="outlined" onClick={() => setUploadMeta((m) => ({ ...m, category: c }))} sx={{ cursor: 'pointer' }} />
                ))}
              </Box>
            )}
            <TextField
              label="Tags (vírgulas)"
              value={uploadMeta.tags}
              onChange={(e) => setUploadMeta((m) => ({ ...m, tags: e.target.value }))}
              fullWidth
              disabled={uploading}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={uploadMeta.active}
                  onChange={(e) => setUploadMeta((m) => ({ ...m, active: e.target.checked }))}
                  disabled={uploading}
                />
              }
              label="Ativo"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadDialog} disabled={uploading}>
            {uploading ? 'A enviar…' : 'Cancelar'}
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || !uploadReadyCount}
          >
            {uploading ? (
              <CircularProgress size={22} />
            ) : (
              `Enviar${uploadReadyCount > 1 ? ` (${uploadReadyCount})` : ''}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Painel para gerenciar TODOS os duplicados num só lugar (item: ações rápidas
          em massa + 1 a 1, sem precisar caçar na lista). Fecha e volta ao envio. */}
      <Dialog open={manageDuplicatesOpen} onClose={() => setManageDuplicatesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Gerenciar duplicados ({uploadDuplicateCount})</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            Aplique a mesma ação a todos de uma vez, ou decida um a um aqui mesmo. Ao resolver todos, você volta para o envio.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={() => applyDuplicateDecisionAll('replace')}>
              Substituir todos
            </Button>
            <Button size="small" variant="outlined" onClick={() => applyDuplicateDecisionAll('copy')}>
              Manter cópia em todos
            </Button>
            <Button size="small" variant="outlined" color="error" onClick={() => applyDuplicateDecisionAll('skip')}>
              Remover todos da fila
            </Button>
          </Stack>
          <Divider sx={{ mb: 1 }} />
          <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
            {uploadFiles.filter((e) => e.mode === 'duplicate').map((entry) => (
              <ListItem
                key={uploadEntryKey(entry)}
                disableGutters
                sx={{ px: 0.5 }}
                secondaryAction={
                  <Stack direction="row" alignItems="center" spacing={0.25}>
                    <Tooltip title="Ver diferenças">
                      <IconButton size="small" color="warning" onClick={() => openDuplicateDialog(entry)}>
                        <WarningAmberIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Substituir existente">
                      <Button size="small" variant="text" sx={{ minWidth: 0, px: 0.75 }} onClick={() => applyDuplicateDecisionQuick(entry, 'replace')}>
                        Subst.
                      </Button>
                    </Tooltip>
                    <Tooltip title="Nova cópia">
                      <Button size="small" variant="text" sx={{ minWidth: 0, px: 0.75 }} onClick={() => applyDuplicateDecisionQuick(entry, 'copy')}>
                        Cópia
                      </Button>
                    </Tooltip>
                    <Tooltip title="Remover da fila">
                      <IconButton size="small" onClick={() => applyDuplicateDecisionQuick(entry, 'skip')}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemText
                  primary={entry.file.name}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  secondary={
                    entry.duplicateCheck?.matches?.[0]
                      ? `Já existe: #${entry.duplicateCheck.matches[0].id}${entry.duplicateCheck.matches[0].title ? ' — ' + entry.duplicateCheck.matches[0].title : ''}`
                      : 'Duplicado'
                  }
                  secondaryTypographyProps={{ variant: 'caption', color: 'warning.dark' }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDuplicatesOpen(false)} variant="contained">Voltar ao envio</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!duplicatePrompt}
        onClose={() => setDuplicatePrompt(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Arquivo já existente</DialogTitle>
        <DialogContent dividers>
          {duplicatePrompt && (
            <Stack spacing={2}>
              <Alert severity="warning">
                Já existe um documento com o arquivo &quot;{duplicatePrompt.file.name}&quot;.
                {duplicatePrompt.check?.match_reason === 'content'
                  ? ' O conteúdo extraído é igual ao de outro documento.'
                  : ' O nome do arquivo coincide com um documento na base.'}
              </Alert>
              {duplicatePrompt.check?.comparison?.summary && (
                <Typography variant="body2">{duplicatePrompt.check.comparison.summary}</Typography>
              )}
              {duplicatePrompt.check?.matches?.[0] && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Documento existente
                  </Typography>
                  <Typography variant="body2">
                    <strong>{duplicatePrompt.check.matches[0].title}</strong>
                    {duplicatePrompt.check.matches[0].original_filename &&
                      ` (${duplicatePrompt.check.matches[0].original_filename})`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Atualizado: {formatDocDate(duplicatePrompt.check.matches[0].updated_at)}
                    {' · '}
                    Categoria: {duplicatePrompt.check.matches[0].category || '—'}
                    {' · '}
                    {duplicatePrompt.check.matches[0].active ? 'Ativo' : 'Inativo'}
                  </Typography>
                  {duplicatePrompt.check.comparison && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Texto: {duplicatePrompt.check.comparison.existing_content_length ?? '—'} caracteres
                      {duplicatePrompt.check.comparison.existing_file_size != null &&
                        ` · Arquivo: ${formatBytes(duplicatePrompt.check.comparison.existing_file_size)}`}
                    </Typography>
                  )}
                  {duplicatePrompt.check.comparison?.existing_preview && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        p: 1,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        fontFamily: 'inherit',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {duplicatePrompt.check.comparison.existing_preview}
                    </Typography>
                  )}
                </Paper>
              )}
              {duplicatePrompt.check?.comparison && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Novo arquivo
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formatBytes(duplicatePrompt.file.size)}
                    {' · '}
                    Texto: {duplicatePrompt.check.comparison.new_content_length ?? '—'} caracteres
                    {duplicatePrompt.check.comparison.identical_content && ' · conteúdo idêntico'}
                  </Typography>
                  {duplicatePrompt.check.comparison.new_preview && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        p: 1,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        fontFamily: 'inherit',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {duplicatePrompt.check.comparison.new_preview}
                    </Typography>
                  )}
                </Paper>
              )}
              <Typography variant="body2" color="text.secondary">
                Substituir atualiza o documento existente (arquivo e texto). Nova cópia cria um documento separado com
                título distinto. Não enviar ignora este arquivo.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Button onClick={() => applyDuplicateDecision('skip')}>Ignorar</Button>
          <Button variant="outlined" onClick={() => applyDuplicateDecision('copy')}>
            Nova cópia
          </Button>
          <Button variant="contained" onClick={() => applyDuplicateDecision('replace')}>
            Substituir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkEditOpen}
        onClose={() => !bulkSaving && setBulkEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar categoria / tags ({selectedIds.length} documentos)</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Marque o que deseja alterar. Os campos não marcados mantêm o valor atual de cada documento.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkEditForm.changeCategory}
                  onChange={(e) => setBulkEditForm((f) => ({ ...f, changeCategory: e.target.checked }))}
                />
              }
              label="Alterar categoria"
            />
            {bulkEditForm.changeCategory && (
              <>
                <TextField
                  size="small"
                  label="Nova categoria"
                  value={bulkEditForm.category}
                  onChange={(e) => setBulkEditForm((f) => ({ ...f, category: e.target.value }))}
                  fullWidth
                  placeholder="Deixe vazio para remover categoria"
                />
                {categoryOptionsForForm.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {categoryOptionsForForm.slice(0, 12).map((c) => (
                      <Chip
                        key={c}
                        size="small"
                        label={c}
                        variant="outlined"
                        onClick={() => setBulkEditForm((f) => ({ ...f, category: c }))}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
            <Divider />
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkEditForm.changeTags}
                  onChange={(e) => setBulkEditForm((f) => ({ ...f, changeTags: e.target.checked }))}
                />
              }
              label="Alterar tags"
            />
            {bulkEditForm.changeTags && (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel id="bulk-tag-mode-label">Modo das tags</InputLabel>
                  <Select
                    labelId="bulk-tag-mode-label"
                    label="Modo das tags"
                    value={bulkEditForm.tagMode}
                    onChange={(e) => setBulkEditForm((f) => ({ ...f, tagMode: e.target.value }))}
                  >
                    <MenuItem value="replace">Substituir tags existentes</MenuItem>
                    <MenuItem value="append">Acrescentar às tags existentes</MenuItem>
                  </Select>
                </FormControl>
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  options={tagOptionsForBulk}
                  value={bulkEditForm.tags}
                  inputValue={bulkEditTagInput}
                  onInputChange={(_, newInput, reason) => {
                    if (reason === 'input' || reason === 'clear') setBulkEditTagInput(newInput);
                  }}
                  onChange={(_, newValue) => {
                    const cleaned = newValue.map((t) => String(t).trim().toLowerCase()).filter(Boolean);
                    setBulkEditForm((f) => ({ ...f, tags: [...new Set(cleaned)] }));
                    setBulkEditTagInput('');
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Tags" placeholder="Enter para confirmar" />
                  )}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEditOpen(false)} disabled={bulkSaving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={applyBulkEdit} disabled={bulkSaving}>
            {bulkSaving ? <CircularProgress size={22} /> : 'Aplicar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkReprocessOpen} onClose={() => !bulkReprocessLoading && setBulkReprocessOpen(false)}>
        <DialogTitle>Reprocessar {selectedIds.length} documento(s)</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Reindexa apenas os documentos selecionados. Pode demorar e consumir tokens de embeddings.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkReprocessOpen(false)} disabled={bulkReprocessLoading}>
            Cancelar
          </Button>
          <Button variant="contained" color="warning" onClick={runBulkReprocess} disabled={bulkReprocessLoading}>
            {bulkReprocessLoading ? <CircularProgress size={22} /> : 'Reprocessar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteOpen} onClose={() => !bulkDeleteLoading && setBulkDeleteOpen(false)}>
        <DialogTitle>Excluir {selectedIds.length} documento(s)</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remover os documentos selecionados? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleteLoading}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={runBulkDelete} disabled={bulkDeleteLoading}>
            {bulkDeleteLoading ? <CircularProgress size={22} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>Remover o documento &quot;{deleteTarget?.title}&quot;? Esta ação não pode ser desfeita.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmReprocessPending} onClose={() => !reprocessPendingLoading && setConfirmReprocessPending(false)}>
        <DialogTitle>Processar documentos pendentes</DialogTitle>
        <DialogContent>
          {reprocessPendingProgress ? (
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Processando {reprocessPendingProgress.done} de {reprocessPendingProgress.total}
                {reprocessPendingProgress.failed > 0 ? ` (${reprocessPendingProgress.failed} com erro)` : ''}…
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  reprocessPendingProgress.total
                    ? (reprocessPendingProgress.done / reprocessPendingProgress.total) * 100
                    : 0
                }
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Processando em lotes. Não feche esta janela.
              </Typography>
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 1 }}>
                Serão processados apenas os {pendingDocs.length} documento(s) ainda não indexado(s) (Pendente/Parcial). Os já indexados não são reprocessados.
              </Alert>
              <Typography variant="body2">Cada documento será indexado em sequência (em lotes). Pode demorar e consumir tokens de embeddings.</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReprocessPending(false)} disabled={reprocessPendingLoading}>
            Cancelar
          </Button>
          <Button variant="contained" color="success" onClick={runReprocessPending} disabled={reprocessPendingLoading || pendingDocs.length === 0}>
            {reprocessPendingLoading ? <CircularProgress size={22} /> : 'Continuar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmReprocessAll} onClose={() => !reprocessAllLoading && setConfirmReprocessAll(false)}>
        <DialogTitle>Reprocessar todos os documentos</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Isso pode demorar e consumir tokens de embeddings. Deseja continuar?
          </Alert>
          <Typography variant="body2">Apenas administradores. Cada documento ativo será reindexado em sequência.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReprocessAll(false)} disabled={reprocessAllLoading}>
            Cancelar
          </Button>
          <Button variant="contained" color="warning" onClick={runReprocessAll} disabled={reprocessAllLoading}>
            {reprocessAllLoading ? <CircularProgress size={22} /> : 'Continuar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
